import ReactDOM, { Root } from 'react-dom/client';
import { ThemeProvider, useTheme } from 'styled-components';

import App from './App';
import { Chat, ChatInner } from './components/Chat/Chat';
import { BetaBadge } from './components/ChatHeader/Header.styled';
import { ChatPanel } from './components/ChatPanel/ChatPanel';
import { LoadingEllipsis } from './components/Message/LoadingEllipsis';
import { OcLoginRequired } from './components/OcWidget/components/LoginRequired/OcLoginRequired';
import { OcFabWidget } from './components/OcWidget/OcFabWidget';
import { renderOcFabChat, unmountOcFabChat } from './components/OcWidget/renderOcFabChat';
import { AppContextProvider } from './store/App/store';
import { GlobalStyleWrapper } from './styles/GlobalStyle';
import { AppConfig, AppEndpoints, EndpointConfigOptions } from './types/app';
import type { RenderOcFabChat } from './types/oc';

export { ChatHeader } from './components/ChatHeader/ChatHeader';
export { OcSkeletonDefs } from './components/Loading/SkeletonSVG';
export type { LoadingMessageProps } from './components/Message/LoadingMessage';
export type {
  ActionStates,
  MessageReference,
  Messages,
  MessageStatus,
  MessageType,
} from './components/Message/types/messages';
export { MESSAGE_ROLE } from './components/Message/types/messages';
export { MessageInputBox } from './components/MessageInputBox/MessageInputBox';
export { useMessageInput } from './components/MessageInputBox/utils/useMessageInput';
export type { OcLoginRequiredProps } from './components/OcWidget/components/LoginRequired/OcLoginRequired';
export type { IOcAppTheme } from './components/OcWidget/config/ocTheme';
export {
  OC_DARK_THEME,
  OC_DEFAULT_THEME,
  OC_LIGHT_THEME_NO_SVG
} from './components/OcWidget/config/ocTheme';
export type { OcFabWidgetProps } from './components/OcWidget/OcFabWidget';
export { Transcript } from './components/TranscriptBox/Transcript';
export type { MessageCustomizationProps } from './components/TranscriptBox/TranscriptMessages';
export type { TranscriptContextValue } from './store/transcript/store';
export { TranscriptContextProvider } from './store/transcript/store';
export type { TranscriptContextState } from './store/transcript/types';
export { useTranscriptContext } from './store/transcript/utils/useTranscriptContext';
export type { IAppTheme } from './styles/IAppTheme';
export type {
  AppConfig,
  AppEndpoints,
  EndpointConfigOptions,
} from './types/app';
export type { OcChatConfig, OcFabWidgetOptions, PreviewState, RenderOcFabChat } from './types/oc';

declare const __MOD_TYPE__: 'UMD' | 'ESM';

declare global {
  interface Window {
    hlAiAssistant: {
      renderInElement: AppConfig;
      unmountApp: (removeElement?: boolean) => void;
      renderOcFabChat: RenderOcFabChat;
      unmountOcFabChat: (removeElement?: boolean) => void;
    };
  }
}

export {
  AppContextProvider,
  BetaBadge,
  Chat,
  ChatInner,
  ChatPanel,
  LoadingEllipsis,
  OcFabWidget,
  OcLoginRequired,
  renderOcFabChat,
  ThemeProvider,
  unmountOcFabChat,
  useTheme,
};

let rootElement: HTMLElement | null;
let rootApp: Root | null;

export const AiAssistant = ({
  endpoints,
  config,
  startOpen = false,
}: {
  endpoints: AppEndpoints;
  config: EndpointConfigOptions;
  startOpen: boolean;
}) => {
  return (
    <AppContextProvider endpoints={endpoints} config={config}>
      <App startOpen={startOpen} />
    </AppContextProvider>
  );
};

/**
 * Render the React App in a specific element
 * @param {HTMLElement} element - Element in which to render the app
 * @param {EndpointConfigOptions} config - Endpoint config for the app
 * @param {AppEndpoints} endpoints - Endpoints for the app
 * @param {boolean} startOpen - Optionally start the app open
 */
export const renderInElement: AppConfig = (
  element: HTMLElement,
  config: EndpointConfigOptions,
  endpoints: AppEndpoints,
  startOpen: boolean = false,
) => {
  const root = ReactDOM.createRoot(element);

  rootElement = element;
  rootApp = root;

  root.render(
    <>
      <GlobalStyleWrapper />
      <AiAssistant
        endpoints={endpoints}
        config={config}
        startOpen={startOpen}
      />
    </>,
  );
};

/**
 * Unmounts the React App
 *
 * Empties or Removes the Root Element of App
 * @param {boolean} [removeElement] - Optionally remove the element from DOM
 */
export const unmountApp = (removeElement?: boolean) => {
  if (rootApp !== null && rootElement !== null) {
    rootApp.unmount();

    if (removeElement) {
      rootElement.remove();
    } else {
      rootElement.innerHTML = '';
    }
  }
};

if (__MOD_TYPE__ === 'UMD') {
  window.hlAiAssistant = {
    renderInElement,
    unmountApp,
    renderOcFabChat,
    unmountOcFabChat,
  };
}
